/**
 * FGTS Digital — Fase 11
 * Geração e transmissão de GRRF (Guia de Recolhimento Rescisório FGTS)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  FileText, Plus, Loader2, Building2, Send, Radio, Trash2, RefreshCw,
  CheckCircle2, Clock, XCircle, ShieldCheck,
} from 'lucide-react';
import { useConfirm } from '../../hooks/useConfirm';
import { CurrencyInput } from '../ui/currency-input';
import { formatCPF, formatDateBR } from '../../lib/helpers';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const MOTIVOS = {
  '02': '02 — Sem justa causa (empregador)',
  '03': '03 — Por justa causa',
  '04': '04 — A pedido do empregado',
  '05': '05 — Término de contrato',
  '10': '10 — Acordo (Lei 13.467/17)',
};

const STATUS_BADGE = {
  rascunho: 'bg-gray-100 text-gray-700',
  transmitida: 'bg-amber-100 text-amber-700',
  paga: 'bg-emerald-100 text-emerald-700',
  vencida: 'bg-red-100 text-red-700',
  rejeitada: 'bg-red-100 text-red-700',
};

const Brl = ({ v }) => <>R$ {Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</>;

const KpiPanel = ({ companyId, refreshKey }) => {
  const [k, setK] = useState({ total_guias: 0, por_status: {}, valor_total_recolher: 0 });
  useEffect(() => {
    if (!companyId) return;
    axios.get(`${API}/api/fgts/kpis`, { ...auth(), params: { company_id: companyId } })
      .then(r => setK(r.data)).catch(() => {});
  }, [companyId, refreshKey]);
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="fgts-kpis">
      <div className="border rounded-lg p-3 bg-slate-50"><div className="text-xs">Total</div><div className="text-2xl font-bold">{k.total_guias}</div></div>
      <div className="border rounded-lg p-3 bg-gray-50"><div className="text-xs">Rascunho</div><div className="text-2xl font-bold">{k.por_status?.rascunho || 0}</div></div>
      <div className="border rounded-lg p-3 bg-amber-50"><div className="text-xs">Transmitidas</div><div className="text-2xl font-bold">{k.por_status?.transmitida || 0}</div></div>
      <div className="border rounded-lg p-3 bg-emerald-50"><div className="text-xs">Pagas</div><div className="text-2xl font-bold">{k.por_status?.paga || 0}</div></div>
      <div className="border rounded-lg p-3 bg-indigo-50"><div className="text-xs">Total a Recolher</div><div className="text-lg font-bold"><Brl v={k.valor_total_recolher} /></div></div>
    </div>
  );
};

const GerarTab = ({ companyId, onGerado }) => {
  const [form, setForm] = useState({
    cpf_empregado: '', nome_empregado: '', matricula: '',
    data_admissao: '', data_demissao: '',
    motivo_rescisao_codigo: '02', aviso_previo: 'indenizado',
    saldo_fgts_devido: 0, fgts_rescisorio_mes_aviso: 0,
    contrib_social_percent: 0, ambiente: '2',
  });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const gerar = async () => {
    if (!companyId) return toast.error('Selecione uma empresa');
    if (!form.cpf_empregado || !form.nome_empregado || !form.data_demissao)
      return toast.error('CPF, nome e data de demissão obrigatórios');
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/fgts/grrf/gerar`, {
        ...form,
        contrib_social_percent: parseFloat(form.contrib_social_percent || 0),
      }, { ...auth(), params: { company_id: companyId } });
      setPreview(r.data);
      toast.success(`GRRF gerada — Total: R$ ${r.data.valores.total_recolher.toFixed(2)}`);
      if (onGerado) onGerado();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro');
    } finally { setLoading(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-indigo-600" />Gerar GRRF</CardTitle>
        <CardDescription>Guia de Recolhimento Rescisório do FGTS — calculada automaticamente.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>CPF Empregado</Label><Input value={formatCPF(form.cpf_empregado)} onChange={e => setForm({ ...form, cpf_empregado: e.target.value })} maxLength={14} data-testid="fgts-cpf-input" /></div>
          <div><Label>Nome Completo</Label><Input value={form.nome_empregado} onChange={e => setForm({ ...form, nome_empregado: e.target.value.replace(/\d/g, '') })} /></div>
          <div><Label>Matrícula</Label><Input value={form.matricula} onChange={e => setForm({ ...form, matricula: e.target.value })} /></div>
          <div><Label>Data Admissão</Label><Input type="date" value={form.data_admissao} onChange={e => setForm({ ...form, data_admissao: e.target.value })} /></div>
          <div><Label>Data Demissão</Label><Input type="date" value={form.data_demissao} onChange={e => setForm({ ...form, data_demissao: e.target.value })} /></div>
          <div>
            <Label>Motivo Rescisão</Label>
            <Select value={form.motivo_rescisao_codigo} onValueChange={v => setForm({ ...form, motivo_rescisao_codigo: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(MOTIVOS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Aviso Prévio</Label>
            <Select value={form.aviso_previo} onValueChange={v => setForm({ ...form, aviso_previo: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="indenizado">Indenizado</SelectItem>
                <SelectItem value="trabalhado">Trabalhado</SelectItem>
                <SelectItem value="dispensado">Dispensado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Saldo FGTS Devido</Label><CurrencyInput value={form.saldo_fgts_devido} onChange={v => setForm({ ...form, saldo_fgts_devido: v })} /></div>
          <div><Label>FGTS Mês Aviso</Label><CurrencyInput value={form.fgts_rescisorio_mes_aviso} onChange={v => setForm({ ...form, fgts_rescisorio_mes_aviso: v })} /></div>
          <div>
            <Label>Contrib. Social LC 110</Label>
            <Select value={String(form.contrib_social_percent)} onValueChange={v => setForm({ ...form, contrib_social_percent: parseFloat(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0% (após 12/2019)</SelectItem>
                <SelectItem value="10">10% (até 12/2019)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={gerar} disabled={loading} data-testid="fgts-gerar-btn">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          <Plus className="h-4 w-4 mr-1" />Gerar GRRF
        </Button>
        {preview && (
          <div className="border rounded-lg p-4 bg-emerald-50 space-y-1">
            <div className="text-sm font-medium">Preview da Guia</div>
            <div className="text-xs space-y-0.5">
              <div>ID interno: <span className="font-mono">{preview.id_grrf}</span></div>
              <div>Multa FGTS ({preview.valores.multa_fgts_percent}%): <Brl v={preview.valores.multa_fgts} /></div>
              <div>Contrib. Social: <Brl v={preview.valores.contrib_social} /></div>
              <div className="font-bold text-base">Total a Recolher: <Brl v={preview.valores.total_recolher} /></div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const GuiasTab = ({ companyId, refreshKey, onChange }) => {
  const [guias, setGuias] = useState([]);
  const [busy, setBusy] = useState(null);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    if (!companyId) return;
    const r = await axios.get(`${API}/api/fgts/guias`, { ...auth(), params: { company_id: companyId, limit: 200 } });
    setGuias(r.data || []);
  }, [companyId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const acao = async (g, op) => {
    setBusy(g.id);
    try {
      if (op === 'transmitir') {
        const r = await axios.post(`${API}/api/fgts/guias/${g.id}/transmitir`, {}, auth());
        toast.success(`Transmitida — nr ${r.data.resposta?.nr_guia || ''}`);
      } else if (op === 'consultar') {
        const r = await axios.post(`${API}/api/fgts/guias/${g.id}/consultar`, {}, auth());
        toast.success(`Status: ${r.data.status}`);
      } else if (op === 'excluir') {
        const ok = await confirm({ title: 'Excluir GRRF?', description: `${g.payload?.trabalhador?.nome} — ${g.id_grrf}` });
        if (!ok) return;
        await axios.delete(`${API}/api/fgts/guias/${g.id}`, auth());
        toast.success('Removida');
      }
      load(); if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro');
    } finally { setBusy(null); }
  };

  return (
    <TooltipProvider>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-600" />Guias Geradas
            <Button size="sm" variant="ghost" onClick={load} className="ml-auto"><RefreshCw className="h-3.5 w-3.5" /></Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b">
              <tr><th className="py-2">ID</th><th>Empregado</th><th>Demissão</th><th>Total</th><th>Status</th><th>nr_guia</th><th>Ações</th></tr>
            </thead>
            <tbody>
              {guias.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-400">Nenhuma guia gerada</td></tr>}
              {guias.map(g => (
              <tr key={g.id} className="border-b" data-testid={`fgts-guia-${g.id}`}>
                <td className="font-mono text-xs">{g.id_grrf?.slice(0, 16)}…</td>
                <td>{g.payload?.trabalhador?.nome}</td>
                <td>{formatDateBR(g.payload?.trabalhador?.data_demissao)}</td>
                <td><Brl v={g.payload?.valores?.total_recolher} /></td>
                <td><Badge className={STATUS_BADGE[g.status] || ''}>{g.status}</Badge></td>
                <td className="font-mono text-xs">{g.nr_guia?.slice(0, 16) || '-'}</td>
                <td className="flex gap-1">
                  {g.status === 'rascunho' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="ghost" disabled={busy === g.id} onClick={() => acao(g, 'transmitir')}>
                          <Send className="h-3.5 w-3.5 text-amber-600" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">Transmitir guia</TooltipContent>
                    </Tooltip>
                  )}
                  {g.status === 'transmitida' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="ghost" disabled={busy === g.id} onClick={() => acao(g, 'consultar')}>
                          <Radio className="h-3.5 w-3.5 text-emerald-600" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">Consultar status</TooltipContent>
                    </Tooltip>
                  )}
                  {(g.status === 'rascunho' || g.status === 'rejeitada') && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="ghost" disabled={busy === g.id} onClick={() => acao(g, 'excluir')}>
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">Excluir guia</TooltipContent>
                    </Tooltip>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      <ConfirmDialog />
    </Card>
  </TooltipProvider>
  );
};

const StatusTab = () => {
  const [s, setS] = useState(null);
  useEffect(() => { axios.get(`${API}/api/fgts/transmissao/status`, auth()).then(r => setS(r.data)); }, []);
  if (!s) return <Loader2 className="h-5 w-5 animate-spin" />;
  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Radio className="h-5 w-5 text-indigo-600" />Status da Transmissão</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Badge className={s.modo === 'real' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>MODO {s.modo?.toUpperCase()}</Badge>
          {s.modo === 'mock' && <span className="text-xs text-slate-500">Defina FGTS_TRANSMISSAO_MODO=real para enviar à Caixa.</span>}
        </div>
        {s.certificado_ativo && <div className="text-sm"><ShieldCheck className="inline h-4 w-4 text-emerald-600 mr-1" />{s.certificado_ativo.titular} <Badge variant="outline" className="ml-2">{s.certificado_ativo.tipo}</Badge></div>}
        <div className="text-xs text-slate-500"><div>Homologação: <span className="font-mono">{s.endpoints?.homologacao}</span></div><div>Produção: <span className="font-mono">{s.endpoints?.producao}</span></div></div>
      </CardContent>
    </Card>
  );
};

export default function FgtsPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    axios.get(`${API}/api/companies`, auth()).then(r => {
      setCompanies(r.data || []); if (r.data?.length) setCompanyId(r.data[0].id);
    });
  }, []);

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto" data-testid="fgts-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileText className="h-7 w-7 text-indigo-600" />FGTS Digital <Badge variant="outline" className="ml-2">Fase 11</Badge></h1>
          <p className="text-sm text-slate-500">GRRF — Guia Rescisória eletrônica</p>
        </div>
        <Select value={companyId || ''} onValueChange={setCompanyId}>
          <SelectTrigger className="w-72" data-testid="fgts-company-select"><Building2 className="h-4 w-4 mr-2" /><SelectValue placeholder="Empresa" /></SelectTrigger>
          <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name || c.razao_social}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <KpiPanel companyId={companyId} refreshKey={refreshKey} />
      <Tabs defaultValue="guias">
        <TabsList>
          <TabsTrigger value="guias" data-testid="fgts-tab-guias">Guias</TabsTrigger>
          <TabsTrigger value="gerar" data-testid="fgts-tab-gerar">Gerar GRRF</TabsTrigger>
          <TabsTrigger value="status" data-testid="fgts-tab-status">Transmissão</TabsTrigger>
        </TabsList>
        <TabsContent value="guias" className="mt-4"><GuiasTab companyId={companyId} refreshKey={refreshKey} onChange={() => setRefreshKey(k => k + 1)} /></TabsContent>
        <TabsContent value="gerar" className="mt-4"><GerarTab companyId={companyId} onGerado={() => setRefreshKey(k => k + 1)} /></TabsContent>
        <TabsContent value="status" className="mt-4"><StatusTab /></TabsContent>
      </Tabs>
    </div>
  );
}
